import { CheckCircleFilled } from '@ant-design/icons'
import { Button, Descriptions, Form, Input, Alert } from 'admin-web-ui'
import { Store } from 'rc-field-form/lib/interface'
import React from 'react'
import { useAsyncFn } from 'react-use'
import styled from 'styled-components'
import {
  DeviceCheckStatus,
  RetrieveDeviceCheckInput,
  WhitelistDeviceStatus,
} from '../../../../api'
import { FormattedDate } from '../../../../components/FormattedDate'
import { useProjectContext } from '../../../../containers/Project'
import { retrieveDeviceCheckStatus } from '../graphqlHandlers/retrieve-device-check-status'
import { retrieveDeviceWhitelistStatus } from '../graphqlHandlers/retrieve-device-whitelist-status'
import { whitelistDevice } from '../graphqlHandlers/whitelist-device'

const SearchButtonContainer = styled.div`
  margin: 10px 0;
  text-align: right;
`

const StyledButton = styled(Button)`
  width: 160px;
`

const StyledAlert = styled(Alert)`
  margin-top: 10px;
`

export const ResultSection = styled.div`
  margin-top: 15px;
  margin-bottom: 20px;
`

export const StyledDescriptions = styled(Descriptions)`
  margin-bottom: 4px;

  .anticon {
    color: ${({ theme }) => theme.colors.sudoBlue};
    font-size: 24px;
  }
`

interface FormData {
  deviceId: string
  type: RetrieveDeviceCheckInput['type']
}

export const IOSVendorIdLookupForm: React.FC = () => {
  const { projectClient } = useProjectContext()

  const [queryState, queryHandler] = useAsyncFn(async (values?: Store) => {
    if (!values) {
      return undefined
    }

    const formData = values as FormData
    const input = {
      deviceId: formData.deviceId,
      type: 'IOS' as RetrieveDeviceCheckInput['type'],
    }

    addToWhitelistHandler(undefined)

    const [deviceCheckStatus, whitelistStatus] = await Promise.all([
      retrieveDeviceCheckStatus(projectClient, input),
      retrieveDeviceWhitelistStatus(projectClient, input),
    ])

    return {
      input,
      deviceCheckStatus,
      whitelistStatus,
    }
  })

  const [addToWhitelistState, addToWhitelistHandler] = useAsyncFn(
    async (event?: React.MouseEvent) => {
      if (!event) {
        return undefined
      }

      if (!queryState.value) {
        throw new Error('Unexpected query state.')
      }

      return whitelistDevice(projectClient, queryState.value.input)
    },
    [queryState.value],
  )

  return (
    <Form onFinish={queryHandler} layout="vertical">
      <Form.Item
        name="deviceId"
        label="Vendor ID"
        rules={[
          { required: true, message: 'Please enter a Vendor ID' },
          {
            pattern:
              /[0-9a-f]{8}\b-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-\b[0-9a-f]{12}/i,
            message:
              'Vendor ID must be a UUID in the format of XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX',
          },
        ]}
      >
        <Input placeholder="Paste in a Vendor ID" />
      </Form.Item>
      <SearchButtonContainer>
        <StyledButton
          type="primary"
          shape="round"
          htmlType="submit"
          loading={queryState.loading}
        >
          Search
        </StyledButton>
      </SearchButtonContainer>

      {queryState.error && (
        <StyledAlert
          type="error"
          message="An error occurred processing your request. Please try again."
          className="VendorIdLookupForm__"
        />
      )}

      {queryState.value && (
        <>
          <DeviceCheckDescription status={queryState.value.deviceCheckStatus} />
          <WhitelistStatusDescription
            status={
              addToWhitelistState.value ?? queryState.value.whitelistStatus
            }
          />
        </>
      )}

      {queryState.value &&
        !queryState.value.whitelistStatus &&
        !addToWhitelistState.value && (
          <StyledButton
            type="primary"
            shape="round"
            onClick={addToWhitelistHandler}
            loading={addToWhitelistState.loading}
            className="VendorIdLookupForm__whitelist-button"
          >
            Add to whitelist
          </StyledButton>
        )}

      {addToWhitelistState.value && (
        <StyledAlert
          type="success"
          message="Device was successfully whitelisted."
          className="VendorIdLookupForm__whitelist-success"
        />
      )}

      {addToWhitelistState.error && (
        <StyledAlert
          type="error"
          message="An error occurred. Please try again."
          className="VendorIdLookupForm__whitelist-error"
        />
      )}
    </Form>
  )
}

export const DeviceCheckDescription: React.FC<{
  status?: DeviceCheckStatus
}> = ({ status }) => (
  <ResultSection>
    <h3>DeviceCheck Status</h3>

    {status ? (
      <StyledDescriptions bordered column={1}>
        <Descriptions.Item label="Bit0">
          {status.bit0 ? 1 : 0}
        </Descriptions.Item>
        <Descriptions.Item label="Bit1">
          {status.bit1 ? 1 : 0}
        </Descriptions.Item>
        <Descriptions.Item label="Last Updated">
          <FormattedDate value={status.lastUpdatedTime} />
        </Descriptions.Item>
      </StyledDescriptions>
    ) : (
      <StyledAlert
        type="info"
        message={
          <>
            There is no DeviceCheck status associated with this Vendor ID.
            <br />
            <strong>Please note:</strong> DeviceCheck status information for
            devices registered before May 2020 may not be available.
          </>
        }
        className="DeviceCheckDescription__not-found-info"
      />
    )}
  </ResultSection>
)

export const WhitelistStatusDescription: React.FC<{
  status?: WhitelistDeviceStatus
}> = ({ status }) => (
  <ResultSection>
    <h3>Whitelist Status</h3>

    {status ? (
      <StyledDescriptions bordered column={1}>
        <Descriptions.Item label="Whitelisted">
          <CheckCircleFilled />
        </Descriptions.Item>
        <Descriptions.Item label="Expiry">
          <FormattedDate value={status.expiry} />
        </Descriptions.Item>
      </StyledDescriptions>
    ) : (
      <StyledAlert
        type="info"
        message="There is no whitelist status associated with this Vendor ID."
        className="WhitelistStatusDescription__not-found-info"
      />
    )}
  </ResultSection>
)
